import {
  FOC_TOUR_LABEL_COP_LAYERS,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import { FOC_REQUIRED_DISPOSITION } from '../orgWorkspaceTour/orgWorkspaceTourSteps';

export const FOC_COP_LAYERS_TOUR_PARAM = 'focCopLayersTour';

/** Opens Hub Layers and starts the 5.4–5.5 FOC COP walkthrough. */
export const FOC_COP_LAYERS_TOUR_HREF = `/hub?openTab=Layers&${FOC_COP_LAYERS_TOUR_PARAM}=1`;

export type FocCopLayersTourSurface = 'layers' | 'aors' | 'sitstat';

export type FocCopLayersTourStepId =
  | 'sectionIntro54'
  | 'cop006'
  | 'cop006a'
  | 'cop006b'
  | 'cop006c'
  | 'cop006d'
  | 'cop007'
  | 'cop007a'
  | 'cop007b'
  | 'sectionIntro55'
  | 'cop008'
  | 'cop008a'
  | 'cop008b'
  | 'cop008c'
  | 'cop008d'
  | 'cop008e'
  | 'cop008f'
  | 'cop009'
  | 'cop009a'
  | 'cop009b'
  | 'focFeedback';

export type FocCopLayersTourStepDef = {
  id: FocCopLayersTourStepId;
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  bullets?: string[];
  /** `data-tour` value. Missing targets become a centered popover. */
  tour?: string;
  surface?: FocCopLayersTourSurface;
};

type RequirementInput = Omit<
  FocCopLayersTourStepDef,
  'title' | 'requirementId'
> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): FocCopLayersTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

/**
 * Unified walk for 5.4 Layers, Data, and Provenance (FOC-COP-006–007) and
 * 5.5 Hierarchical COP and Geographic Navigation (FOC-COP-008–009).
 */
export const FOC_COP_LAYERS_TOUR_STEPS: FocCopLayersTourStepDef[] = [
  {
    id: 'sectionIntro54',
    surface: 'layers',
    tour: 'hub-layers-workspace',
    title: '5.4 Layers, Data, and Provenance',
    description:
      'Organize Hub map layers into mission packages, name them clearly, and expose source, currency, and availability so users trust what they see on the Common Operating Picture.',
  },
  requirement({
    id: 'cop006',
    requirementId: 'FOC-COP-006',
    name: 'Mission-Organized Layer Packages',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-layers-workspace',
    description:
      'PRATUS organizes geospatial layers into mission-relevant packages and folders so operators can enable the overlays that match their current mission without hunting through an undifferentiated catalog.',
  }),
  requirement({
    id: 'cop006a',
    requirementId: 'FOC-COP-006a',
    name: 'Layer Grouping',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-folder',
    description:
      'Layers can be grouped into folders so related overlays (for example weather, AIS, or unit boundaries) stay together and can be toggled as a package.',
  }),
  requirement({
    id: 'cop006b',
    requirementId: 'FOC-COP-006b',
    name: 'Layer Presets',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-presets',
    description:
      'Workspace and folder filters act as layer presets: narrow the list to an incident workspace or mission folder so the COP starts from a known, reusable set of overlays.',
  }),
  requirement({
    id: 'cop006c',
    requirementId: 'FOC-COP-006c',
    name: 'Layer Naming',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-row',
    description:
      'Each layer carries a clear display name (and provenance badge when from an external source) so users can distinguish overlays and understand what they are enabling on the map.',
  }),
  requirement({
    id: 'cop006d',
    requirementId: 'FOC-COP-006d',
    name: 'Organizational and AOR-Aware Default Layers',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-presets',
    description:
      'Default and workspace-scoped layers can follow organizational and AOR context—Home AOR, incident assignment, and Hub filters—so users land on overlays appropriate to their echelon and assignment.',
  }),
  requirement({
    id: 'cop007',
    requirementId: 'FOC-COP-007',
    name: 'Source, Currency, and Availability Metadata',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-metadata',
    description:
      'Layer detail surfaces source system, last updated, region/AOR, incident scope, and type so operators can judge currency and availability before relying on the overlay.',
  }),
  requirement({
    id: 'cop007a',
    requirementId: 'FOC-COP-007a',
    name: 'Data Quality and Availability',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-quality',
    description:
      'When an external layer fails to load or has restricted access, PRATUS surfaces load errors and provenance badges so users know the overlay is unavailable or degraded rather than silently missing.',
  }),
  requirement({
    id: 'cop007b',
    requirementId: 'FOC-COP-007b',
    name: 'Source-Specific Restrictions',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'layers',
    tour: 'hub-cop-layer-restrictions',
    description:
      'External layers record auth type and permission controls (public, API key, or user ArcGIS auth) so source-specific access restrictions are visible and enforceable.',
  }),
  {
    id: 'sectionIntro55',
    surface: 'aors',
    tour: 'aor-workspace',
    title: '5.5 Hierarchical COP and Geographic Navigation',
    description:
      'Navigate the Coast Guard geographic hierarchy—from incident detail through Sector, District, Area, and national context—with click-to-drill maps, breadcrumbs, and safe SitStat landing views.',
  },
  requirement({
    id: 'cop008',
    requirementId: 'FOC-COP-008',
    name: 'Incident-to-National Hierarchy',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'aors',
    tour: 'aor-hierarchy',
    description:
      'The Hub presents an incident-to-national geographic hierarchy so users can move between tactical and strategic context without leaving the Common Operating Picture.',
  }),
  requirement({
    id: 'cop008a',
    requirementId: 'FOC-COP-008a',
    name: 'Increasing Strategic Context',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'aors',
    tour: 'aor-area-filters',
    description:
      'Area filters and top-echelon selection increase strategic context—stepping from a focused Sector or District up toward Area and national awareness.',
  }),
  requirement({
    id: 'cop008b',
    requirementId: 'FOC-COP-008b',
    name: 'Incident Detail',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'aors',
    tour: 'aor-workspace',
    description:
      'Incident-level detail remains available within the hierarchy: assigned incidents and workspace filters keep the COP focused on the active response while Hub geography stays reachable.',
  }),
  requirement({
    id: 'cop008c',
    requirementId: 'FOC-COP-008c',
    name: 'Sector Detail',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'aors',
    tour: 'aor-sector-details',
    description:
      'Open a Sector card to review sector-level details, subordinate units, and map extent for that portion of the hierarchy.',
  }),
  requirement({
    id: 'cop008d',
    requirementId: 'FOC-COP-008d',
    name: 'District Detail',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'aors',
    tour: 'aor-district-details',
    description:
      'District detail shows the district node, its sectors, and the roll-up appropriate to that echelon.',
  }),
  requirement({
    id: 'cop008e',
    requirementId: 'FOC-COP-008e',
    name: 'National Detail',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'aors',
    tour: 'aor-area-filters',
    description:
      'Area and national narrative sit at the top of the hierarchy: Area filters and All Areas map views provide the broadest geographic COP context.',
  }),
  requirement({
    id: 'cop008f',
    requirementId: 'FOC-COP-008f',
    name: 'Area-Level View',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'aors',
    tour: 'aor-area-filters',
    description:
      'Select an Area to focus the hierarchy and map on that Area’s districts and units—the Area-level view used for regional situational awareness.',
  }),
  requirement({
    id: 'cop009',
    requirementId: 'FOC-COP-009',
    name: 'Click-to-Drill Navigation',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'aors',
    tour: 'aor-hierarchy',
    description:
      'Click nodes in the hierarchy (and related map polygons) to drill into subordinate AORs while keeping geographic context on the Hub map.',
  }),
  requirement({
    id: 'cop009a',
    requirementId: 'FOC-COP-009a',
    name: 'Breadcrumbs',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'aors',
    tour: 'aor-hierarchy-chips',
    description:
      'Hierarchy chips act as breadcrumbs: they show the current path through Area → District → Sector → SubUnit and let users jump back up the tree safely.',
  }),
  requirement({
    id: 'cop009b',
    requirementId: 'FOC-COP-009b',
    name: 'Safe Landing Views',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'sitstat',
    tour: 'sitstat-workspace',
    description:
      'SitStat Views (including the AOR View) provide safe landing layouts: a saved rail/widget arrangement scoped to an AOR so users return to a known, operational picture instead of an empty board.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_COP_LAYERS),
  },
];

/** Product Tours nav shows this walkthrough only when FOC requirements are on. */
export function focCopLayersTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focCopLayersTourStepIds(): FocCopLayersTourStepId[] {
  return FOC_COP_LAYERS_TOUR_STEPS.map((step) => step.id);
}

export function focCopLayersTourStepTitles(): string[] {
  return FOC_COP_LAYERS_TOUR_STEPS.map((step) => step.title);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Popover body: the disposition line first, then the requirement text. */
export function focCopLayersTourPopoverHtml(
  step: Pick<
    FocCopLayersTourStepDef,
    'disposition' | 'description' | 'bullets'
  >
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}
